// Stats.jsx 📊 CTI Dashboard by Country
import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./Threats.css";

export default function Stats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);

  const countryToFlag = (code) => {
    if (!code) return "https://flagcdn.com/w80/un.png";
    return `https://flagcdn.com/w80/${code.toLowerCase()}.png`;
  };

  const loadImageAsBase64 = (url) =>
    fetch(url)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          })
      );

  const waitForImagesToLoad = (container) =>
    Promise.all(
      Array.from(container.querySelectorAll("img")).map((img) =>
        img.complete ? Promise.resolve() : new Promise((res) => (img.onload = res))
      )
    );

  const generatePDF = async () => {
    const input = document.getElementById("cti-report");
    const logoUrl = "/img/logo_cti.png";

    if (!input) return alert("Missing content to export.");

    try {
      input.querySelectorAll("img").forEach((img) => {
        img.setAttribute("crossorigin", "anonymous");
      });

      await waitForImagesToLoad(input);
      const logoBase64 = await loadImageAsBase64(logoUrl);

      const originalWidth = input.style.width;
      input.style.width = "1100px";

      const canvas = await html2canvas(input, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#1e1e1e",
      });

      input.style.width = originalWidth;

      const imgData = canvas.toDataURL("image/jpeg", 0.50);
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const marginX = 10;

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth - marginX * 2;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      // Dark BG
      pdf.setFillColor(30, 30, 30);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");

      // Header
      const paddingTop = 10;
      const logoMaxW = 40;
      const logoMaxH = 20;
      const logoRatio = 1186 / 2290;
      let logoW = logoMaxW;
      let logoH = logoW * logoRatio;
      if (logoH > logoMaxH) {
        logoH = logoMaxH;
        logoW = logoH / logoRatio;
      }

      if (logoBase64) {
        pdf.addImage(logoBase64, "JPEG", marginX, paddingTop, logoW, logoH);
      }

      // 📌 Title + Period
      const titleY = paddingTop + 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor("#c77a3a");
      pdf.text("Circet CTI - Threat Landscape", marginX + logoW + 5, titleY);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor("#f8f9fa");
      pdf.text(`Period: Last ${timeRange} days`, marginX + logoW + 5, titleY + 6);

      pdf.addImage(imgData, "JPEG", marginX, paddingTop + logoH + 6, imgWidth, imgHeight);

      const disclaimer = "© 2025 CIRCET CTI – Confidential – Internal Use Only";
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(9);
      pdf.setTextColor("#666");
      const discW = pdf.getTextWidth(disclaimer);
      pdf.text(disclaimer, (pageWidth - discW) / 2, pageHeight - 10);

      const iso = new Date().toISOString().split("T")[0];
      pdf.save(`cti_report_${iso}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      alert("Error generating PDF.");
    }
  };

  useEffect(() => {
    setLoading(true);
    fetch(`/api/stats_threats.php?time_range=${timeRange}`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Unauthorized");
        return res.json();
      })
      .then(setData)
      .catch((err) => {
        console.error("CTI Fetch Error:", err);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [timeRange]);

  if (loading) {
    return (
      <div className="profile-wrapper text-center">
        <h4 className="text-light">Loading CTI data...</h4>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="profile-wrapper text-center text-danger">
        <h4>Error loading CTI data</h4>
      </div>
    );
  }

  const countryMap = new Map();

  const safeSet = (rawCode, field, value) => {
    if (!rawCode) return;
    const code = rawCode.toUpperCase();
    const entry = countryMap.get(code) || { location: code, vuln: 0, ioc: 0, resolved: 0, acknowledged: 0, ongoing: 0 };
    entry[field] = value;
    countryMap.set(code, entry);
  };

  data.vulns_by_country.forEach(({ location, vuln_count }) =>
    safeSet(location, "vuln", vuln_count)
  );
  data.iocs_by_country.forEach(({ affected_country, ioc_count }) =>
    safeSet(affected_country, "ioc", ioc_count)
  );
  data.resolved_by_country.forEach(({ location, resolved_count }) =>
    safeSet(location, "resolved", resolved_count)
  );
  data.acknowledged_by_country.forEach(({ location, acknowledged_count }) =>
    safeSet(location, "acknowledged", acknowledged_count)
  );
  data.ongoing_by_country.forEach(({ location, ongoing_count }) =>
    safeSet(location, "ongoing", ongoing_count)
  );

  const countries = [...countryMap.values()].sort((a, b) => b.vuln - a.vuln);

  return (
    <div className="profile-wrapper">
      {/* Filters */}
      <div className="cti-filters mb-4">
        <div className="cti-filters-inner">
          <span className="cti-title">CTI Threat Landscape</span>
          <div className="d-flex align-items-center gap-2">
            <select
              className="cti-select"
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>

            <button className="btn-export" onClick={generatePDF}>
              Generate PDF
            </button>
          </div>
        </div>
      </div>

      {/* Country Grid */}
      <div id="cti-report">
	  	<div className="cti-contrib-inline mb-4">
		  <span className="cti-contrib-title-inline">🏆 Top Contributors</span>
		  <div className="cti-contrib-badges">
			{data.top_contributors.map((contrib, i) => (
			  <span key={i} className="contrib-pill">
				<span className="pill-rank">#{i + 1}</span>
				<span className="pill-name">{contrib.display_name}</span>
				<span className="pill-count">({contrib.contribution_count} IoCs)</span>
			  </span>
			))}
		  </div>
		</div>
        <div className="cti-grid-container">
          {countries.map((c, idx) => (
            <div key={idx} className="cti-card">
              <div className="mb-2 d-flex flex-column align-items-center">
                <img
                  src={countryToFlag(c.location)}
                  alt={c.location}
                  className="cti-flag"
                  crossOrigin="anonymous"
                />
                <div className="mt-2 text-uppercase text-light fw-bold">{c.location}</div>
              </div>

              <div className="w-100 mt-3">
                <ThreatStat label="Vulnerabilities" value={c.vuln} color="#f44336" icon="bi-bug-fill" />
				<ThreatStat label="In Progress" value={c.ongoing} color="#ffeb3b" icon="bi-hourglass-split" />
				<ThreatStat label="Resolved" value={c.resolved} color="#4caf50" icon="bi-patch-check-fill" />
                <ThreatStat label="IoCs" value={c.ioc} color="#ffa726" icon="bi-shield-exclamation" />
				<ThreatStat label="IoCs Ack." value={c.acknowledged} color="#42a5f5" icon="bi-shield-check" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ThreatStat({ label, value, color, icon }) {
  return (
    <div className="cti-stat-row">
      <span className="cti-stat-label">
        <i className={`bi ${icon} me-1`} style={{ color }}></i>
        {label}
      </span>
      <span style={{ fontWeight: "bold", color }}>{value}</span>
    </div>
  );
}
